import { useMemo } from 'react';
import { CoreDialogContent } from '@youscience/core';

import useEducationPlannerStore from '@store/EducationPlannerStore';
import useApplyingSchoolsPlannerStore from '@store/ApplyingSchoolsPlannerStore';

import { STEPS } from '@interfaces/selectSchool';
import DialogFooter from '@components/DialogFooter';

import { SelectionStep1 } from './components/SelectionStep1Planner';
import { SelectionStep2 } from './components/SelectionStep2Planner';

import { sxStyles, StyledDialog, StyledDialogTitle } from './SchoolSelectionPlanner.styles';

interface SchoolSelectionProps {
  isOpenSchoolSelection: boolean;
  onCloseSchoolSelection: () => void;
}

export const SchoolSelection = (props: SchoolSelectionProps) => {
  const { isOpenSchoolSelection, onCloseSchoolSelection } = props;

  const {
    schoolSelectionData: { activeStep = STEPS.step1 },
  } = useEducationPlannerStore((state) => state);

  const { appliedSchools, getAppliedSchool } = useApplyingSchoolsPlannerStore((state) => state);

  const {
    schoolSelectionData: { school, schoolPreviewInfo },
    setSchoolSelectionData,
    saveSchoolSelection,
  } = useEducationPlannerStore((state) => state);

  const isAppliedSchool = useMemo(() => {
    return appliedSchools.some((item) => {
      if (school) {
        return item.id === +school.value;
      }
    });
  }, [school?.value]);

  const handleConfirmSelection = async () => {
    try {
      await saveSchoolSelection();

      await getAppliedSchool();

      onCloseSchoolSelection();
    } catch (err) {
      console.error(err);
    }
  };

  const handleGoToStep1 = () => {
    void setSchoolSelectionData({
      activeStep: STEPS.step1,
    });
  };

  const handleGoToStep2 = () => {
    void setSchoolSelectionData({
      activeStep: STEPS.step2,
    });
  };

  const isDisableNextButton = useMemo(() => {
    return schoolPreviewInfo.id && school?.value
      ? schoolPreviewInfo.id.toString() !== school.value || isAppliedSchool
      : true;
  }, [schoolPreviewInfo.id, school?.value, isAppliedSchool]);

  const APPLYING_STEPS_DATA = {
    [STEPS.step1]: {
      title: 'Select the school you want to apply to',
      content: <SelectionStep1 isShowHelperText={isAppliedSchool} />,
      footer: (
        <DialogFooter
          goBackButtonTitle="Cancel"
          goNextButtonTitle="Next"
          isDisableNextButton={isDisableNextButton}
          onGoBack={onCloseSchoolSelection}
          onGoNext={handleGoToStep2}
        />
      ),
    },
    [STEPS.step2]: {
      title: 'Confirm your choice',
      content: <SelectionStep2 />,
      footer: (
        <DialogFooter
          goBackButtonTitle="Back"
          goNextButtonTitle="Confirm"
          goNextActionButtonTitle="Confirming"
          onGoBack={handleGoToStep1}
          onGoNext={handleConfirmSelection}
        />
      ),
    },
  };

  return (
    <StyledDialog open={isOpenSchoolSelection} onClose={onCloseSchoolSelection} scroll="paper">
      <StyledDialogTitle data-testid="schoolSelectionTitle" id="school-selection">
        {APPLYING_STEPS_DATA[activeStep].title}
      </StyledDialogTitle>

      <CoreDialogContent sx={sxStyles.contentBody}>{APPLYING_STEPS_DATA[activeStep].content}</CoreDialogContent>

      {APPLYING_STEPS_DATA[activeStep].footer}
    </StyledDialog>
  );
};
